import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { Modal } from 'UIPackage/ModalModule/components/Modal';

import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { ISelectOption, SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import styles from './RefreshNonErvkObjectModal.module.scss';
import { IKnmState } from '../../MainModule/types/IKnmState';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import {
  knmClearErvkObjectsOfControl,
  knmGetControlObjectsByNonErvkOrganization,
} from '../../MainModule/actions/knmActions';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getErvkObjectsOfControl } from '../../MainModule/selectors/knmSelectors';
import { IErvkObjectsOfControlSubjectData } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControlSubjectData';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';
import { generateOrganizationTemplateText } from '../../../KnmErknmPackage/MainModule/common/generateOrganizationTemplateText';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { decisionGetControlObjectsByNonErvkOrganization } from '../../../DecisionPackage/MainModule/actions/decisionActions';

interface IRefreshNonErvkObjectModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (
    targetOrganization: IOrganizationForRefreshNonErvkObjectModal,
    targetObject: IKnmErknmFormObject,
    replacementObjects: IErvkObjectsOfControl[],
    replacementSubjects: IErvkObjectsOfControlSubjectData[],
  ) => void;
  object: IKnmErknmFormObject;
  nonErvkOrganizations: IOrganizationForRefreshNonErvkObjectModal[];
  isErknm: boolean;
  isPm: boolean;
}

interface IRefreshNonErvkObjectModalStateProps {
  ervkObjectsOfControl: IKnmState['ervkObjectsOfControl'] | undefined;
}

export const RefreshNonErvkObjectModalWithoutStore = ({
  visible,
  onClose,
  onSubmit,
  object,
  nonErvkOrganizations,
  isErknm,
  isPm,
  ervkObjectsOfControl,
}: IRefreshNonErvkObjectModalProps & IRefreshNonErvkObjectModalStateProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedErvkObjects, setSelectedErvkObjects] = useState<IErvkObjectsOfControl[]>([]);
  const [objectSubjects, setObjectSubjects] = useState<IErvkObjectsOfControlSubjectData[]>([]);
  const [selectedObjectSubjects, setSelectedObjectSubjects] = useState<IErvkObjectsOfControlSubjectData[]>([]);
  const [isTableVisible, setIsTableVisible] = useState<boolean>(false);
  const [isObjectSubjectsTableVisible, setIsObjectSubjectsTableVisible] = useState<boolean>(false);

  const organizationsSelectOptions: ISelectOption<number>[] = nonErvkOrganizations.map((v, index) => {
    return {
      id: index,
      value: v,
      label: generateOrganizationTemplateText(
        v.inn ?? undefined,
        v.ogrn ?? undefined,
        v.organizationName ?? v.originalName ?? undefined,
        v.rafpNza ?? undefined,
      ),
    };
  });

  const [filterOrganization, setFilterOrganization] = useState<ISelectOption<number> | undefined>();

  useEffect(() => {
    // базовая очистка при открытии
    if (visible) {
      setIsTableVisible(false);
      setIsObjectSubjectsTableVisible(false);
      setSelectedErvkObjects([]);
      setObjectSubjects([]);
      setSelectedObjectSubjects([]);
      dispatch(knmClearErvkObjectsOfControl());

      if (organizationsSelectOptions.length === 1) {
        setFilterOrganization(organizationsSelectOptions[0]);
      } else {
        setFilterOrganization(undefined);
      }
    } else {
      dispatch(knmClearErvkObjectsOfControl());
    }
  }, [visible]);

  const handleLoad = useCallback(() => {
    if (filterOrganization !== undefined && filterOrganization !== null) {
      setIsTableVisible(true);
      setIsObjectSubjectsTableVisible(false);
      setSelectedErvkObjects([]);

      const org = nonErvkOrganizations[filterOrganization?.id];
      const subjectTypeCode = org.subjectType?.code ?? org.subjectTypePm?.code ?? org.organizationType?.code ?? null;

      if (subjectTypeCode !== null) {
        const isDecisionPassport: boolean = !isErknm && !isPm;

        if (isDecisionPassport) {
          dispatch(
            decisionGetControlObjectsByNonErvkOrganization(
              subjectTypeCode,
              org.inn ?? null,
              subjectTypeCode !== 'IP' ? org.ogrn ?? null : null,
              subjectTypeCode === 'IP' ? org.ogrn ?? null : null,
              org.kpp ?? null,
              org.snils ?? null,
              org.rafpNza ?? null,
              org.originalName ?? null,
              org.document ?? null,
            ),
          );
        } else {
          dispatch(
            knmGetControlObjectsByNonErvkOrganization(
              subjectTypeCode,
              org.inn ?? null,
              subjectTypeCode !== 'IP' ? org.ogrn ?? null : null,
              subjectTypeCode === 'IP' ? org.ogrn ?? null : null,
              org.kpp ?? null,
              org.snils ?? null,
              org.rafpNza ?? null,
              org.organizationName ?? null,
              org.document ?? null,
            ),
          );
        }
      }
    }
  }, [dispatch, filterOrganization, nonErvkOrganizations, isErknm, isPm]);

  const clearFilter = () => {
    setFilterOrganization(undefined);
  };

  const isErvkObjectsEquals = (val1: IErvkObjectsOfControl, val2: IErvkObjectsOfControl): boolean => {
    return val1.ervkId === val2.ervkId;
  };

  const isSelected = (value: IErvkObjectsOfControl): boolean => {
    return !!selectedErvkObjects.find(item => isErvkObjectsEquals(item, value));
  };

  const handleSelect = (value: IErvkObjectsOfControl) => {
    if (isSelected(value)) {
      setSelectedErvkObjects(selectedErvkObjects.filter(item => !isErvkObjectsEquals(item, value)));
    } else setSelectedErvkObjects([...selectedErvkObjects, value]);
  };

  const handleSelectAll = () => {
    setSelectedErvkObjects(ervkObjectsOfControl || []);
  };

  const handleDismissAll = () => {
    setSelectedErvkObjects([]);
  };

  const isSelectedObjectSubjects = (value: IErvkObjectsOfControlSubjectData): boolean => {
    return !!selectedObjectSubjects.find(item => isObjectSubjectsEquals(item, value));
  };

  const handleSelectObjectSubjects = (value: IErvkObjectsOfControlSubjectData) => {
    if (isSelectedObjectSubjects(value)) {
      setSelectedObjectSubjects(selectedObjectSubjects.filter(item => !isObjectSubjectsEquals(item, value)));
    } else setSelectedObjectSubjects([...selectedObjectSubjects, value]);
  };

  const handleSelectAllObjectSubjects = () => {
    setSelectedObjectSubjects(objectSubjects || []);
  };

  const handleDismissAllObjectSubjects = () => {
    setSelectedObjectSubjects([]);
  };

  const isObjectSubjectsEquals = (
    val1: IErvkObjectsOfControlSubjectData,
    val2: IErvkObjectsOfControlSubjectData,
  ): boolean => {
    return val1.ervkId === val2.ervkId;
  };

  const organizationInfo = (org: IErvkObjectsOfControlSubjectData) => {
    const inn = org.inn ? `ИНН: ${org.inn} ` : '';
    const ogrn = org.ogrn ? `ОГРН: ${org.ogrn} ` : '';
    const name = org.organizationFullName ? `Наименование: ${org.organizationFullName} ` : '';
    const snils = org.snils ? `СНИЛС: ${org.snils} ` : '';
    const kpp = org.kpp ? `КПП: ${org.kpp} ` : '';
    const rafp = org.rafp ? `НЗА: ${org.rafp} ` : '';
    const fio = org.firstName ? `ФИО: ${org.firstName} ${org.middleName} ${org.surName} ` : '';
    const nationality = org.nationality
      ? `Государство, в котором указанное юридическое лицо зарегистрировано: ${org.nationality} `
      : '';

    return inn + ogrn + name + fio + snils + kpp + rafp + nationality;
  };

  const handleSubmit = () => {
    if (filterOrganization) {
      const uniqueSubjects = Array.from(
        new Map(selectedErvkObjects.flatMap(v => v.subjects).map(v => [v.id, v])).values(),
      );

      if (uniqueSubjects.length === 1) {
        onSubmit(nonErvkOrganizations[filterOrganization.id], object, selectedErvkObjects, uniqueSubjects);
      } else {
        setObjectSubjects(uniqueSubjects);
        setIsTableVisible(false);
        setIsObjectSubjectsTableVisible(true);
      }
    }
  };

  const handleSubmitObjectSubjects = () => {
    if (filterOrganization) {
      const uniqueSubjects = Array.from(
        new Map(selectedErvkObjects.flatMap(v => v.subjects).map(v => [v.id, v])).values(),
      );
      const selectedObjectSubjectErvkIds = selectedObjectSubjects.map(v => v.ervkId);
      const subjectsToSubmit = uniqueSubjects.filter(v => selectedObjectSubjectErvkIds.includes(v.ervkId));

      onSubmit(nonErvkOrganizations[filterOrganization.id], object, selectedErvkObjects, subjectsToSubmit);
    }
  };

  return (
    <Modal visible={visible} onClose={onClose} modalBodyClassName={styles.ModalBody} modalClassName={styles.Modal}>
      <ModalHeader title={isObjectSubjectsTableVisible ? 'Выбор контролируемого лица' : 'Выбор объекта контроля'} />
      <ModalBody overflow={isTableVisible ? 'scroll' : 'visible'}>
        <Row>
          <FieldBlock
            label="Контролируемое лицо"
            color="black"
            type="not-space"
            labelSize="col-3"
            placementLabel="left"
            className={cn(styles.SearchFieldBlock, {
              [styles.SearchFieldBlockWithAsterisk]: !filterOrganization,
            })}
          >
            <SelectInput
              options={organizationsSelectOptions}
              value={filterOrganization}
              size="medium"
              className={styles.SearchField}
              onChange={setFilterOrganization}
              invalid={!filterOrganization}
              error="Поле не может быть пустым"
            />
          </FieldBlock>
        </Row>
        <div className={styles.SearchButtonsRow}>
          <Button
            text="Искать"
            color="primary"
            size="medium"
            type="button"
            className={styles.LeftButton}
            onClick={handleLoad}
            disabled={!filterOrganization}
          />
          <Button
            text="Очистить фильтр"
            size="medium"
            type="button"
            onClick={clearFilter}
            disabled={!filterOrganization}
          />
        </div>
        {isTableVisible && (
          <>
            <Row notSpace>
              <Button
                text="Выбрать все"
                size="medium"
                type="button"
                className={styles.LeftButton}
                onClick={handleSelectAll}
              />
              <Button text="Сбросить" size="medium" type="button" onClick={handleDismissAll} />
            </Row>
            <div className={styles.Table}>
              <div className={styles.TableHeader}>
                <div></div>
                <div>№</div>
                <div>НАИМЕНОВАНИЕ</div>
                <div>КОНТРОЛИРУЕМОЕ ЛИЦО</div>
                <div>МЕСТОНАХОЖДЕНИЯ</div>
                <div>КАТЕГОРИЯ РИСКА</div>
                <div>КЛАСС ОПАСНОСТИ</div>
              </div>
              {!ervkObjectsOfControl?.length ? (
                <div className={styles.TableBodyEmpty}>
                  <span>Данные отсутствуют</span>
                </div>
              ) : (
                <div className={styles.TableBody}>
                  <div className={styles.TableContent}>
                    {ervkObjectsOfControl.map((item, index) => (
                      <div
                        key={`ervkObjectsOfControlItem[${index}]`}
                        className={cn(styles.TableRow, {
                          [styles.TableRowActive]: isSelected(item),
                        })}
                        onClick={() => handleSelect(item)}
                      >
                        <div>
                          <Checkbox theme="system" checked={isSelected(item)} />
                        </div>
                        <div>{item.cosId}</div>
                        <div>{item.name}</div>
                        {/* Несмотря на то, что у ОК может быть несколько КЛ, согласно требованиям выводим любое первое,
                         но это касается только отображения в этом списке ("обычная" форма поиска ОК работает так же) */}
                        <div>{item.subjects.length ? organizationInfo(item.subjects[0]) : ''}</div>
                        <div>{item.address}</div>
                        <div>{item.risk?.title}</div>
                        <div>{item.dangerClass?.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        {isObjectSubjectsTableVisible && (
          <>
            <Row notSpace>
              <Button
                text="Выбрать все"
                size="medium"
                type="button"
                className={styles.LeftButton}
                onClick={handleSelectAllObjectSubjects}
              />
              <Button text="Сбросить" size="medium" type="button" onClick={handleDismissAllObjectSubjects} />
            </Row>
            <div className={styles.Table}>
              <div className={[styles.TableHeader, styles.ObjectSubjectsTableHeader].join(' ')}>
                <div></div>
                <div>Контролируемое лицо</div>
                <div>Идентификатор в ЕРВК</div>
              </div>
              {!objectSubjects?.length ? (
                <div className={styles.TableBodyEmpty}>
                  <span>Данные отсутствуют</span>
                </div>
              ) : (
                <div className={styles.TableBody}>
                  <div className={[styles.TableContent, styles.ObjectSubjectsTableContent].join(' ')}>
                    {objectSubjects.map((item, index) => (
                      <div
                        key={`objectSubject[${index}]`}
                        className={cn(styles.TableRow, {
                          [styles.TableRowActive]: isSelectedObjectSubjects(item),
                        })}
                        onClick={() => handleSelectObjectSubjects(item)}
                      >
                        <div>
                          <Checkbox theme="system" checked={isSelectedObjectSubjects(item)} />
                        </div>
                        <div>{organizationInfo(item)}</div>
                        <div>{item.ervkId}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>
      {isTableVisible && (
        <ModalActions>
          <Button
            text="Добавить"
            color="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={!selectedErvkObjects.length}
          />
          <Button text="Отмена" size="medium" onClick={onClose} />
        </ModalActions>
      )}
      {isObjectSubjectsTableVisible && (
        <ModalActions>
          <Button
            text="Добавить"
            color="primary"
            size="medium"
            onClick={handleSubmitObjectSubjects}
            disabled={isErknm ? !selectedObjectSubjects.length : selectedObjectSubjects.length !== 1}
          />
          <Button text="Отмена" size="medium" onClick={onClose} />
        </ModalActions>
      )}
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IRefreshNonErvkObjectModalStateProps => ({
  ervkObjectsOfControl: getErvkObjectsOfControl(state),
});

export const RefreshNonErvkObjectModal = connect(mapStateToProps)(RefreshNonErvkObjectModalWithoutStore);

export interface IOrganizationForRefreshNonErvkObjectModal {
  inn?: string | null;
  ogrn?: string | null;
  kpp?: string | null;
  rafpNza?: string | null;
  snils?: string | null;
  document?: string | null;
  subjectType?: IDictionarySelectOption<ITargetEntity> | null;
  subjectTypePm?: IDictionarySelectOption<ITargetEntity> | null;
  organizationType?: IDictionarySelectOption<ITargetEntity> | null;
  organizationName?: string | null;
  originalName?: string | null;
}
